import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  getJobs, 
} from '../../redux/features/jobSlice';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { fetchClientsAsync } from '../../redux/features/clientSlice';
import { 
  ClipboardList, 
  Users, 
  UserCheck, 
  Plus,
  Clock, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Gift,
  X,
  ShoppingCart,
  Package,
  Wrench,
  Warehouse,
  FileText,
  MessageSquare,
  Mail,
  Phone,
  Store,
  Calendar,
  Award,
  DollarSign
} from 'lucide-react';
import { parseMoneyAmountSR } from '../../utils/displayUtils';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { getRoutePath } from '../../utils/routingUtils';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import ComparativeMetrics from './ComparativeMetrics';
import { getCurrentPackage as getCurrentPackageUtil, isFeatureAllowed, isMarketingChannelAllowed } from '../../utils/packageLimits';
import ChangePlanModal from '../billing/ChangePlanModal';
import { getRolePermissionsFromStorage, canViewCompanyDashboard } from '../../utils/profileRoleUtils';
import { countWorkloadJobs, countWorkloadPeople, formatRotationSeconds, getMonkeyRotationDuration, getWorkloadPerPerson } from '../../utils/monkeyRotation';
import MonkeyWorkloadCard from './MonkeyWorkloadCard';
import { getDashboardJobStats, getWorkloadJobCount } from '../../redux/api';
import { getPackageFeatureItems, PAID_PACKAGE_NAMES, PACKAGE_PRICE_LABELS } from '../../utils/packageFeatureLists';

const API = axios.create({ baseURL: `${API_BASE_URL}/api` });

// Add token to requests
API.interceptors.request.use((req) => {
  const profile = localStorage.getItem('profile');
  if (profile) {
    try {
      const parsedProfile = JSON.parse(profile);
      if (parsedProfile.token) {
        req.headers.Authorization = `Bearer ${parsedProfile.token}`;
      }
    } catch (e) {
      // Silent fail
    }
  }
  return req;
});

const DashboardHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { jobs, loading: jobsLoading } = useSelector((state) => state.job);
  const { workers, loading: workersLoading } = useSelector((state) => state.worker || { workers: [], loading: false });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { clients, loading: clientsLoading } = useSelector((state) => state.client || { clients: [], loading: false });
  const { user } = useSelector((state) => state.auth);
  
  // Check if user is a worker without full company dashboard access
  const userTypeFromStorage = typeof window !== 'undefined' ? localStorage.getItem('userType') : null;
  const rolePermissions = getRolePermissionsFromStorage();
  const canViewCompanyStats = canViewCompanyDashboard(userTypeFromStorage, rolePermissions);

  const isWorker = React.useMemo(() => {
    if (userTypeFromStorage === 'worker' || userTypeFromStorage === 'partner') return true;
    // Fallback: check user structure
    const userResult = user?.result;
    if (userResult) {
      const hasWorkerStructure = userResult.firstName && userResult.lastName && !userResult.companyName;
      return hasWorkerStructure;
    }
    return false;
  }, [user, userTypeFromStorage]);
  
  // Helper function to get current package
  const getCurrentPackage = () => getCurrentPackageUtil(user);
  
  // Check if user is demo account
  const isDemoUser = user?.result?.email === 'demo@gmail.com';
  
  // Initialize selectedPackage from current package
  const [selectedPackage, setSelectedPackage] = useState(() => getCurrentPackage());
  const [stats, setStats] = useState({
    activeJobs: 0,
    completedJobs: 0,
    pendingJobs: 0,
    totalWorkers: 0,
    activeWorkers: 0,
    totalClients: 0,
    totalRevenue: 0,
    totalCost: 0,
    totalProfit: 0,
    todayJobs: 0,
    yearJobs: 0,
    weekJobs: 0,
    webshopOrders: 0,
    webshopProducts: 0,
    spareParts: 0,
    inventoryItems: 0,
    customsDeclarations: 0,
    smsSent: 0,
    viberSent: 0,
    emailSent: 0,
    whatsappSent: 0
  });
  const [betaTrialInfo, setBetaTrialInfo] = useState(null);
  const [showBetaBanner, setShowBetaBanner] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const [jobCountsLoading, setJobCountsLoading] = useState(true);
  const [quickWorkloadCount, setQuickWorkloadCount] = useState(null);
  const [workersFetchStarted, setWorkersFetchStarted] = useState(false);
  const [clientsFetchStarted, setClientsFetchStarted] = useState(false);
  const [jobsFetchStarted, setJobsFetchStarted] = useState(false);
  const [allowExtraFetch, setAllowExtraFetch] = useState(false);
  const [extraReady, setExtraReady] = useState({
    webshopOrders: false,
    webshopProducts: false,
    spareParts: false,
    inventoryItems: false,
    customsDeclarations: false,
    smsSent: false,
    viberSent: false,
    emailSent: false,
    whatsappSent: false
  });

  const businessType = getBusinessType();
  const workloadJobCount = React.useMemo(() => {
    if (Array.isArray(jobs) && jobsFetchStarted && !jobsLoading) {
      return countWorkloadJobs(jobs);
    }
    return quickWorkloadCount ?? 0;
  }, [jobs, jobsFetchStarted, jobsLoading, quickWorkloadCount]);
  const workforce = React.useMemo(
    () => countWorkloadPeople(workers, partners),
    [workers, partners]
  );
  const rotationDuration = React.useMemo(
    () => getMonkeyRotationDuration(getWorkloadPerPerson(workloadJobCount, workforce.people)),
    [workloadJobCount, workforce.people]
  );
  const rotationSecondsLabel = formatRotationSeconds(rotationDuration);
  
  // Update selectedPackage when user changes or demo package changes
  useEffect(() => {
    if (user?.result) {
      const currentPackage = getCurrentPackage();
      setSelectedPackage(currentPackage);
    }
  }, [user?.result?.email, user?.result?.package]);
  
  // Listen for demo package changes in localStorage (for demo users)
  useEffect(() => {
    if (isDemoUser) {
      const handleStorageChange = (e) => {
        if (e.key === 'demoPackage') {
          setSelectedPackage(e.newValue || 'Starter');
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      const onDemoPackageCustom = () => {
        const demoPackage = localStorage.getItem('demoPackage') || 'Starter';
        setSelectedPackage(demoPackage);
      };
      window.addEventListener('spintasker-demo-package', onDemoPackageCustom);
      // Also listen for changes in the same window
      const interval = setInterval(() => {
        const demoPackage = localStorage.getItem('demoPackage') || 'Starter';
        if (selectedPackage !== demoPackage) {
          setSelectedPackage(demoPackage);
        }
      }, 100);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('spintasker-demo-package', onDemoPackageCustom);
        clearInterval(interval);
      };
    }
  }, [isDemoUser, selectedPackage]);

  // Calculate beta trial info
  useEffect(() => {
    if (user?.result) {
      // Use trialEndDate if available, otherwise fallback to created date + 30 days
      const trialEndDate = user.result.trialEndDate ? new Date(user.result.trialEndDate) : null;
      const trialStartDate = user.result.trialStartDate ? new Date(user.result.trialStartDate) : null;
      const createdDate = user.result.created ? new Date(user.result.created) : null;

      if (trialEndDate && trialStartDate) {
        const now = new Date();
        const isInBetaPeriod = now < trialEndDate && user.result.trialActive !== false;
        
        if (isInBetaPeriod) {
          setBetaTrialInfo({
            startDate: trialStartDate,
            endDate: trialEndDate,
            isActive: true
          });
        } else {
          setBetaTrialInfo({
            startDate: trialStartDate,
            endDate: trialEndDate,
            isActive: false
          });
        }
      } else if (createdDate) {
        // Fallback: calculate 30 days from creation date
        const now = new Date();
        const thirtyDaysFromCreation = new Date(createdDate);
        thirtyDaysFromCreation.setDate(thirtyDaysFromCreation.getDate() + 30);
        
        const isInBetaPeriod =
          now < thirtyDaysFromCreation &&
          user.result.trialActive !== false;

        if (isInBetaPeriod) {
          setBetaTrialInfo({
            startDate: createdDate,
            endDate: thirtyDaysFromCreation,
            isActive: true
          });
        } else {
          setBetaTrialInfo({
            startDate: createdDate,
            endDate: thirtyDaysFromCreation,
            isActive: false
          });
        }
      }
    }
  }, [user]);

  // Update countdown timer
  useEffect(() => {
    if (betaTrialInfo?.isActive) {
      const updateTimer = () => {
        const now = new Date();
        const endDate = betaTrialInfo.endDate;
        const diff = endDate - now;

        if (diff > 0) {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);

          setTimeRemaining({ days, hours, minutes, seconds });
        } else {
          setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          setBetaTrialInfo(prev => prev ? { ...prev, isActive: false } : null);
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    }
  }, [betaTrialInfo]);

  useEffect(() => {
    if (workersLoading) setWorkersFetchStarted(true);
  }, [workersLoading]);

  useEffect(() => {
    if (clientsLoading) setClientsFetchStarted(true);
  }, [clientsLoading]);

  useEffect(() => {
    if (jobsLoading) setJobsFetchStarted(true);
  }, [jobsLoading]);

  // Load data immediately when component mounts
  useEffect(() => {
    const profile = localStorage.getItem("profile");
    const isAuth = !!profile;
    const currentPath = window.location.pathname;
    const isTenantRoute = currentPath.match(/^\/([^/]+)\/dashboard/);
    
    if (isTenantRoute) {
      const currentCompanySlug = localStorage.getItem('currentCompanySlug');
      const currentCompanyId = localStorage.getItem('currentCompanyId');
      
      if (!currentCompanySlug || !currentCompanyId) {
        const retryTimeout = setTimeout(() => {
          const retrySlug = localStorage.getItem('currentCompanySlug');
          const retryId = localStorage.getItem('currentCompanyId');
          if (retrySlug && retryId) loadData();
        }, 500);
        return () => clearTimeout(retryTimeout);
      }
    }
    
    if (isAuth) {
      loadData();
    }
    
    function loadData() {
      let businessTypeValue = businessType;
      if (!businessTypeValue) businessTypeValue = getBusinessType();
      if (!businessTypeValue) {
        try {
          const profileData = profile ? JSON.parse(profile) : null;
          businessTypeValue = profileData?.result?.businessType || sessionStorage.getItem('businessType');
        } catch (e) {}
      }
      dispatch(getWorkers());
      dispatch(getPartners());
      dispatch(fetchClientsAsync());

      const loadFastStats = async () => {
        try {
          const [statsRes, countRes] = await Promise.all([
            getDashboardJobStats(businessTypeValue),
            getWorkloadJobCount(businessTypeValue)
          ]);
          if (statsRes?.data) {
            setStats((prev) => ({
              ...prev,
              activeJobs: statsRes.data.activeJobs ?? prev.activeJobs,
              completedJobs: statsRes.data.completedJobs ?? prev.completedJobs,
              pendingJobs: statsRes.data.pendingJobs ?? prev.pendingJobs,
              todayJobs: statsRes.data.todayJobs ?? prev.todayJobs,
              yearJobs: statsRes.data.yearJobs ?? prev.yearJobs,
              weekJobs: statsRes.data.weekJobs ?? prev.weekJobs
            }));
          }
          if (countRes?.data?.count != null) {
            setQuickWorkloadCount(countRes.data.count);
          }
        } catch (e) {
          // Fallback: full jobs list will fill the cards
        } finally {
          setJobCountsLoading(false);
          setAllowExtraFetch(true);
        }

        if (businessTypeValue) dispatch(getJobs(businessTypeValue));
      };

      loadFastStats();
    }
  }, [dispatch, businessType]);

  useEffect(() => {
    if (!Array.isArray(jobs) || jobsLoading || !jobsFetchStarted) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activeJobs = jobs.filter(job =>
      job.status !== 'Completed' && job.status !== 'Cancelled'
    ).length;

    const completedJobs = jobs.filter(job =>
      job.status === 'Completed'
    ).length;

    const pendingJobs = jobs.filter(job =>
      job.status === 'In Pending' || job.status === 'Received'
    ).length;

    const todayJobs = jobs.filter(job => {
      if (!job.serviceDate) return false;
      const jobDate = new Date(job.serviceDate);
      jobDate.setHours(0, 0, 0, 0);
      return jobDate.getTime() === today.getTime();
    }).length;

    const currentYear = today.getFullYear();
    const yearJobs = jobs.filter(job => {
      if (!job.serviceDate) return false;
      const jobDate = new Date(job.serviceDate);
      return jobDate.getFullYear() === currentYear;
    }).length;

    const startOfWeek = new Date(today);
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    startOfWeek.setDate(today.getDate() + diff);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const weekJobs = jobs.filter(job => {
      if (!job.serviceDate) return false;
      const jobDate = new Date(job.serviceDate);
      return jobDate >= startOfWeek && jobDate <= endOfWeek;
    }).length;

    const completedJobsForStats = jobs.filter(job =>
      job.status === 'Completed' || job.status === 'Finished' || job.status === 'Archived'
    );

    const { totalRevenue, totalCost } = completedJobsForStats
      .reduce((acc, job) => {
        if (!job.report) {
          return acc;
        }

        let revenue = 0;
        if (job.report.serviceCharged) {
          revenue = parseMoneyAmountSR(job.report.serviceCharged);
        } else if (job.servicePrice) {
          revenue = parseFloat(job.servicePrice) || 0;
        }

        let reversiCost = job.report.reversiCost || 0;
        if (reversiCost === 0 && job.report.usedSpareParts && Array.isArray(job.report.usedSpareParts) && job.report.usedSpareParts.length > 0) {
          reversiCost = job.report.usedSpareParts.reduce((sum, part) => {
            if (!part) return sum;
            const price = typeof part.price === 'number' ? part.price : parseFloat(part.price) || 0;
            const quantity = typeof part.quantity === 'number' ? part.quantity : parseFloat(part.quantity) || 1;
            return sum + (price * quantity);
          }, 0);
        }

        let additionalMaterialsCost = job.report.additionalMaterialsCost || 0;
        if (additionalMaterialsCost === 0 && job.report.additionalMaterials && Array.isArray(job.report.additionalMaterials) && job.report.additionalMaterials.length > 0) {
          additionalMaterialsCost = job.report.additionalMaterials.reduce((sum, material) => {
            if (!material) return sum;
            const price = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
            return sum + price;
          }, 0);
        }

        const amortizationCost = job.report.amortizationCost || 0;
        const jobCost = job.report.totalCost || (reversiCost + additionalMaterialsCost + amortizationCost);

        return {
          totalRevenue: acc.totalRevenue + revenue,
          totalCost: acc.totalCost + jobCost
        };
      }, { totalRevenue: 0, totalCost: 0 });

    setStats(prev => ({
      ...prev,
      activeJobs,
      completedJobs,
      pendingJobs,
      totalRevenue,
      totalCost,
      totalProfit: totalRevenue - totalCost,
      todayJobs,
      yearJobs,
      weekJobs
    }));
  }, [jobs, jobsLoading, jobsFetchStarted]);

  useEffect(() => {
    if (!Array.isArray(workers) || workersLoading || !workersFetchStarted) return;
    const activeWorkers = workers.filter(worker =>
      worker.status === 'active' || worker.status === 'available'
    ).length;
    setStats(prev => ({
      ...prev,
      totalWorkers: workers.length,
      activeWorkers
    }));
  }, [workers, workersLoading, workersFetchStarted]);

  useEffect(() => {
    if (!Array.isArray(clients) || clientsLoading || !clientsFetchStarted) return;
    setStats(prev => ({
      ...prev,
      totalClients: clients.length
    }));
  }, [clients, clientsLoading, clientsFetchStarted]);

  // Fetch additional data in waves of 4 so cards fill in as soon as each group returns
  useEffect(() => {
    const markExtra = (patch) => {
      setStats((prev) => ({ ...prev, ...patch }));
      setExtraReady((prev) => {
        const next = { ...prev };
        Object.keys(patch).forEach((key) => {
          if (key in next) next[key] = true;
        });
        return next;
      });
    };

    const runSafe = async (fn) => {
      try {
        await fn();
      } catch (e) {}
    };

    const fetchAdditionalData = async () => {
      const profile = localStorage.getItem('profile');
      if (!profile) return;
      const token = JSON.parse(profile)?.token;
      if (!token) return;

      await Promise.all([
        runSafe(async () => {
          const ordersRes = await API.get('/webshop/orders');
          markExtra({ webshopOrders: ordersRes.data?.length || 0 });
        }),
        runSafe(async () => {
          const productsRes = await API.get('/webshop/products/admin');
          markExtra({ webshopProducts: productsRes.data?.length || 0 });
        }),
        runSafe(async () => {
          const sparePartsRes = await API.get('/spare-part');
          markExtra({ spareParts: sparePartsRes.data?.length || 0 });
        }),
        runSafe(async () => {
          const inventoryRes = await API.get('/inventory/items');
          markExtra({ inventoryItems: inventoryRes.data?.length || 0 });
        })
      ]);

      await Promise.all([
        runSafe(async () => {
          const customsRes = await API.get('/inventory/customs');
          markExtra({ customsDeclarations: customsRes.data?.length || 0 });
        }),
        runSafe(async () => {
          const marketingRes = await API.get('/marketing');
          const messages = marketingRes.data || [];
          markExtra({
            smsSent: messages.filter((m) => m.type === 'sms').length,
            viberSent: messages.filter((m) => m.type === 'viber').length,
            emailSent: messages.filter((m) => m.type === 'email').length,
            whatsappSent: messages.filter((m) => m.type === 'whatsapp').length
          });
        })
      ]);

      setExtraReady((prev) => ({
        ...prev,
        webshopOrders: true,
        webshopProducts: true,
        spareParts: true,
        inventoryItems: true,
        customsDeclarations: true,
        smsSent: true,
        viberSent: true,
        emailSent: true,
        whatsappSent: true
      }));
    };

    if (user?.result && allowExtraFetch) {
      fetchAdditionalData();
    }
  }, [user, allowExtraFetch]);

  const recentJobs = jobs
    ?.filter(job => job.status !== 'Completed' && job.status !== 'Cancelled')
    .sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at))
    .slice(0, 5) || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
      case 'In Repair':
      case 'Diagnosing':
        return 'bg-blue-100 text-blue-800';
      case 'In Pending':
      case 'Received':
        return 'bg-yellow-100 text-yellow-800';
      case 'On Road':
      case 'At Client':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'In Pending':
      case 'Received':
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const StatCard = ({ icon, title, value, subtitle, color = "blue", onClick, disabled = false, loading = false }) => {
    const colorClasses = {
      blue: "bg-blue-50 border-blue-200 text-blue-700",
      green: "bg-green-50 border-green-200 text-green-700",
      purple: "bg-purple-50 border-purple-200 text-purple-700",
      orange: "bg-orange-50 border-orange-200 text-orange-700",
      yellow: "bg-yellow-50 border-yellow-200 text-yellow-700",
      red: "bg-red-50 border-red-200 text-red-700"
    };

    const clickable = Boolean(onClick) && !disabled;

    return (
      <div 
        onClick={clickable ? onClick : undefined}
        className={`${colorClasses[color]} border rounded-lg p-2.5 sm:p-4 transition-shadow select-text ${
          disabled 
            ? 'opacity-50 cursor-not-allowed grayscale' 
            : clickable
              ? 'cursor-pointer hover:shadow-md'
              : 'cursor-default hover:shadow-sm'
        }`}
        title={disabled ? 'Ova funkcionalnost nije dostupna u vašem paketu' : ''}
      >
        {/* Mobile View */}
        <div className="flex flex-row items-center justify-between gap-2 sm:hidden">
          <div className="p-1.5 rounded-lg bg-white flex-shrink-0">
            {icon}
          </div>
          <h3 className="text-xs font-medium opacity-75 min-w-0 flex-1 text-center leading-tight">{title}</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {loading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <p className="text-xl font-bold whitespace-nowrap">{value}</p>
            )}
            {onClick && (
              <ArrowRight className="w-4 h-4 opacity-50 flex-shrink-0" />
            )}
          </div>
        </div>

        {/* Desktop View - Better Flex Layout */}
        <div className="hidden sm:flex flex-row items-center justify-between gap-3 lg:gap-4">
          <div className="p-2 rounded-lg bg-white flex-shrink-0">
            {icon}
          </div>
          <h3 className="text-xs lg:text-sm font-medium opacity-75 whitespace-nowrap flex-1 text-center">{title}</h3>
          <div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
            {loading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <p className="text-base lg:text-lg xl:text-xl font-bold whitespace-nowrap">{value}</p>
            )}
            {onClick && (
              <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 opacity-50 flex-shrink-0" />
            )}
          </div>
        </div>
      </div>
    );
  };

  const QuickActionButton = ({ icon, label, onClick, color = "blue" }) => {
    const colorClasses = {
      blue: "bg-blue-500 hover:bg-blue-600",
      green: "bg-green-500 hover:bg-green-600",
      purple: "bg-purple-500 hover:bg-purple-600"
    };

    return (
      <button
        onClick={onClick}
        className={`${colorClasses[color]} text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-sm hover:shadow-md`}
      >
        {icon}
        {label}
      </button>
    );
  };

  const workersCardLoading = !workersFetchStarted || workersLoading;
  const clientsCardLoading = !clientsFetchStarted || clientsLoading;
  const financeCardLoading = !jobsFetchStarted || jobsLoading;
  const monkeyLoading = quickWorkloadCount == null && financeCardLoading;

  const currentPackage = getCurrentPackage();
  const packageColors = {
    'Free': 'from-blue-600 to-purple-600',
    'Starter': 'from-green-600 to-emerald-600',
    'Standard': 'from-blue-500 to-cyan-500',
    'Business': 'from-purple-600 to-pink-600',
    'Premium': 'from-yellow-500 to-orange-500'
  };
  const packageNames = {
    'Free': 'Free',
    'Starter': 'Starter',
    'Standard': 'Standard',
    'Business': 'Business',
    'Premium': 'Premium'
  };

  const currentPackageId = Number(user?.result?.package);
  const safePackageId = Number.isFinite(currentPackageId) && currentPackageId >= 1 && currentPackageId <= 5 ? currentPackageId : 1;

  return (
    <div className="container mx-auto px-3 py-3 sm:px-4 sm:py-4 space-y-2 sm:space-y-3">
      <ChangePlanModal
        open={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        currentPackageId={safePackageId}
      />
      {/* Current Package Banner - Show for all users (not workers) */}
      {canViewCompanyStats && (isDemoUser ? (betaTrialInfo?.isActive && showBetaBanner) : true) && (
        <div className={`bg-gradient-to-r ${packageColors[currentPackage] || 'from-blue-600 to-purple-600'} rounded-lg p-3 sm:p-5 text-white shadow-lg relative overflow-hidden`}>
          {isDemoUser && (
            <button
              onClick={() => setShowBetaBanner(false)}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-200 transition-colors z-10 p-1"
              aria-label="Zatvori"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <div className={`flex flex-col gap-2.5 sm:gap-4 ${isDemoUser ? 'pr-8 sm:pr-12' : ''}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 sm:gap-4">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div className="bg-white/20 rounded-full p-2 sm:p-3">
                  {isDemoUser ? <Gift className="w-5 h-5 sm:w-6 sm:h-6" /> : <Award className="w-5 h-5 sm:w-6 sm:h-6" />}
                </div>
                <div>
                  <h3 className="text-base sm:text-xl font-bold leading-snug">
                    {isDemoUser ? 'Besplatna demo verzija' : `Vaš trenutni plan: ${packageNames[currentPackage]}`}
                  </h3>
                </div>
              </div>
              <div className="flex flex-row gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() =>
                    isDemoUser
                      ? navigate(getRoutePath('payments/fakture'))
                      : setShowPlanModal(true)
                  }
                  className="flex-1 sm:flex-none sm:w-auto bg-white text-blue-600 px-3 sm:px-6 py-2 rounded-lg text-sm sm:text-base font-semibold hover:bg-blue-50 transition-colors shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  {isDemoUser ? 'Nadogradi' : 'Promeni plan'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPackageDetails((open) => !open)}
                  className="flex-1 sm:flex-none sm:w-auto bg-white/15 text-white border border-white/30 px-3 sm:px-6 py-2 rounded-lg text-sm sm:text-base font-semibold hover:bg-white/25 transition-colors flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  {showPackageDetails ? 'Sakrij pakete' : 'Paketi'}
                </button>
              </div>
            </div>

            {!isDemoUser && betaTrialInfo?.isActive && (
              <div className="rounded-lg bg-white/15 px-3 py-2 sm:px-4 sm:py-3 text-sm flex flex-col gap-1.5 sm:gap-2 md:flex-row md:flex-wrap md:items-center md:gap-x-6 md:gap-y-2">
                <p className="font-semibold text-white shrink-0">Besplatni probni period (30 dana)</p>
                <p className="text-white/90 font-mono text-base tabular-nums shrink-0">
                  {timeRemaining.days}d {String(timeRemaining.hours).padStart(2, '0')}:
                  {String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
                </p>
                <p className="text-white/75 text-xs md:text-sm shrink-0">
                  Do {betaTrialInfo.endDate ? new Date(betaTrialInfo.endDate).toLocaleString('sr-RS') : '—'}
                </p>
              </div>
            )}
            {!isDemoUser &&
              Number(user?.result?.package || 1) === 1 &&
              betaTrialInfo &&
              !betaTrialInfo.isActive &&
              user?.result?.trialEndDate &&
              new Date() >= new Date(user.result.trialEndDate) && (
                <div className="rounded-lg bg-amber-500/20 border border-amber-400/35 px-4 py-2 text-xs text-amber-50">
                  <p className="font-medium">Probni period je istekao</p>
                  <p className="text-amber-100/90 mt-1">
                    Izaberite plaćeni plan („Promeni plan”). Podaci su sačuvani.
                  </p>
                </div>
              )}
            
            {isDemoUser && (
              <div className="flex flex-wrap items-center gap-2">
                {['Starter', 'Standard', 'Business', 'Premium'].map((pkg) => {
                  const isSelected = selectedPackage === pkg;
                  
                  return (
                    <button
                      key={pkg}
                      onClick={() => {
                        setSelectedPackage(pkg);
                        localStorage.setItem('demoPackage', pkg);
                        setShowPackageDetails(true);
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-white text-blue-600 shadow-md'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                      title="Kliknite da promenite demo paket"
                    >
                      {pkg}
                    </button>
                  );
                })}
              </div>
            )}

            {showPackageDetails && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
                {PAID_PACKAGE_NAMES.map((name) => {
                  const isCurrent = (isDemoUser ? selectedPackage : currentPackage) === name;
                  return (
                    <div
                      key={name}
                      className={`rounded-lg px-3 py-3 text-sm ${
                        isCurrent
                          ? 'bg-white text-gray-900 shadow-md'
                          : 'bg-white/10 text-white'
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <p className="font-bold">{name}</p>
                        <p className={`text-xs whitespace-nowrap ${isCurrent ? 'text-gray-600' : 'text-white/80'}`}>
                          {PACKAGE_PRICE_LABELS[name]}
                        </p>
                      </div>
                      {isCurrent && (
                        <p className="text-xs font-semibold text-emerald-700 mb-2">Vaš trenutni plan</p>
                      )}
                      <ul className={`list-disc list-inside space-y-1 text-xs ${isCurrent ? 'text-gray-700' : 'text-white/90'}`}>
                        {getPackageFeatureItems(name, t).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between !mt-2 sm:!mt-1">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 leading-snug">
            Dobrodošli, {user?.result?.companyName || user?.result?.firstName || 'Korisnik'}!
          </h1>
        </div>
      </div>

      <div className="!mt-2 sm:!mt-1">
        <MonkeyWorkloadCard
          jobCount={workloadJobCount}
          rotationDuration={rotationDuration}
          rotationSecondsLabel={rotationSecondsLabel}
          workforce={workforce}
          loading={monkeyLoading}
        />
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 !mt-3 sm:!mt-3">
        {/* Always show these 4 cards */}
        <StatCard
          icon={<ClipboardList className="w-6 h-6" />}
          title="Ukupni Poslovi"
          value={stats.activeJobs}
          subtitle={`${stats.todayJobs} danas`}
          color="blue"
          loading={jobCountsLoading}
        />
        <StatCard
          icon={<Calendar className="w-6 h-6" />}
          title={`Poslovi u ${new Date().getFullYear()}`}
          value={stats.yearJobs}
          subtitle={`U ${new Date().getFullYear()}. godini`}
          color="blue"
          loading={jobCountsLoading}
        />
        <StatCard
          icon={<Calendar className="w-6 h-6" />}
          title="Poslovi ove Nedelje"
          value={stats.weekJobs}
          subtitle="Ova nedelja"
          color="blue"
          loading={jobCountsLoading}
        />
        <StatCard
          icon={<TrendingUp className="w-6 h-6" />}
          title="Poslovi Danas"
          value={stats.todayJobs}
          color="blue"
          loading={jobCountsLoading}
        />
        
        {/* Show additional stats only for admin/company users, not for workers */}
        {canViewCompanyStats && (
          <>
            <StatCard
              icon={<Clock className="w-6 h-6" />}
              title="Poslovi na Čekanju"
              value={stats.pendingJobs}
              color="yellow"
              loading={jobCountsLoading}
            />
            <StatCard
              icon={<UserCheck className="w-6 h-6" />}
              title="Radnici"
              value={stats.totalWorkers}
              subtitle={`${stats.activeWorkers} aktivnih`}
              color="green"
              loading={workersCardLoading}
              onClick={() => navigate(getRoutePath('workers'))}
            />
            <StatCard
              icon={<Users className="w-6 h-6" />}
              title="Klijenti"
              value={stats.totalClients}
              subtitle="Ukupno klijenata"
              color="purple"
              loading={clientsCardLoading}
              onClick={() => navigate(getRoutePath('clients'))}
            />
            <StatCard
              icon={<div className="text-xs font-bold text-orange-700">RSD</div>}
              title="Ukupan Prihod"
              value={`${(stats.totalRevenue || 0).toLocaleString('sr-RS')}`}
              subtitle="Ukupno prihoda"
              color="orange"
              loading={financeCardLoading}
            />
            <StatCard
              icon={<TrendingDown className="w-6 h-6" />}
              title="Trošak"
              value={`${(stats.totalCost || 0).toLocaleString('sr-RS')}`}
              subtitle="Ukupni troškovi"
              color="red"
              loading={financeCardLoading}
            />
            <StatCard
              icon={<DollarSign className="w-6 h-6" />}
              title="Čista Zarada"
              value={`${(stats.totalProfit || 0).toLocaleString('sr-RS')}`}
              subtitle="Ukupna zarada"
              color="green"
              loading={financeCardLoading}
            />
            <StatCard
              icon={<CheckCircle className="w-6 h-6" />}
              title="Završeni Poslovi"
              value={stats.completedJobs}
              subtitle="Ukupno završenih"
              color="green"
              loading={jobCountsLoading}
            />
            <StatCard
              icon={<ShoppingCart className="w-6 h-6" />}
              title="Web Shop Porudžbine"
              value={stats.webshopOrders}
              subtitle="Ukupno porudžbina"
              color="blue"
              loading={!extraReady.webshopOrders}
              disabled={!isFeatureAllowed('webshop', user)}
              onClick={() => navigate(getRoutePath('company/webshop'))}
            />
            <StatCard
              icon={<Package className="w-6 h-6" />}
              title="Web Shop Proizvodi"
              value={stats.webshopProducts}
              subtitle="Ukupno proizvoda"
              color="purple"
              loading={!extraReady.webshopProducts}
              disabled={!isFeatureAllowed('webshop', user)}
              onClick={() => navigate(getRoutePath('company/webshop'))}
            />
            <StatCard
              icon={<Wrench className="w-6 h-6" />}
              title="Rezervni Delovi"
              value={stats.spareParts}
              subtitle="Ukupno delova"
              color="orange"
              loading={!extraReady.spareParts}
              disabled={!isFeatureAllowed('roba', user)}
              onClick={() => navigate(getRoutePath('spare-parts'))}
            />
            <StatCard
              icon={<Warehouse className="w-6 h-6" />}
              title="Magacin"
              value={stats.inventoryItems}
              subtitle="Ukupno artikala"
              color="green"
              loading={!extraReady.inventoryItems}
              disabled={!isFeatureAllowed('roba', user)}
              onClick={() => navigate(getRoutePath('company/inventory'))}
            />
            <StatCard
              icon={<FileText className="w-6 h-6" />}
              title="Uvoz"
              value={stats.customsDeclarations}
              subtitle="Uvozni obračuni"
              color="blue"
              loading={!extraReady.customsDeclarations}
              disabled={!isFeatureAllowed('roba', user)}
              onClick={() => navigate(getRoutePath('company/inventory/customs'))}
            />
            <StatCard
              icon={<Phone className="w-6 h-6" />}
              title="SMS Poruke"
              value={stats.smsSent}
              subtitle="Poslato SMS poruka"
              color="purple"
              loading={!extraReady.smsSent}
              disabled={!isMarketingChannelAllowed('sms', user)}
              onClick={() => navigate(getRoutePath('marketing/sms'))}
            />
            <StatCard
              icon={<MessageSquare className="w-6 h-6" />}
              title="Viber Poruke"
              value={stats.viberSent}
              subtitle="Poslato Viber poruka"
              color="green"
              loading={!extraReady.viberSent}
              disabled={!isMarketingChannelAllowed('viber', user)}
              onClick={() => navigate(getRoutePath('marketing/viber'))}
            />
            <StatCard
              icon={<Mail className="w-6 h-6" />}
              title="Email Poruke"
              value={stats.emailSent}
              subtitle="Poslato email poruka"
              color="blue"
              loading={!extraReady.emailSent}
              disabled={!isMarketingChannelAllowed('email', user)}
              onClick={() => navigate(getRoutePath('marketing/email'))}
            />
            <StatCard
              icon={<Store className="w-6 h-6" />}
              title="WhatsApp Poruke"
              value={stats.whatsappSent}
              subtitle="Poslato WhatsApp poruka"
              color="green"
              loading={!extraReady.whatsappSent}
              disabled={!isMarketingChannelAllowed('whatsapp', user)}
              onClick={() => navigate(getRoutePath('marketing/whatsapp'))}
            />
          </>
        )}
      </div>

      {canViewCompanyStats && (
        <div className="mt-3 sm:mt-4">
          <ComparativeMetrics />
        </div>
      )}

    </div>
  );
};

export default DashboardHome;
